import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { experienceDesignXdb001Api, type SprintDashboard, type SprintValidation, type Xdb001Status } from '../../api/experience-design-xdb001';

type Tab = 'overview' | 'modules' | 'domains' | 'providers' | 'jobs' | 'gates' | 'status';

export function ExperienceDesignXdb001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [domains, setDomains] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Xdb001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-violet-200 bg-white dark:border-violet-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-violet-700 dark:text-violet-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, dom, prov, jb, gt, st] = await Promise.all([
        experienceDesignXdb001Api.dashboard(), experienceDesignXdb001Api.experienceModules(),
        experienceDesignXdb001Api.experienceDomains(), experienceDesignXdb001Api.providers(),
        experienceDesignXdb001Api.backgroundJobs(), experienceDesignXdb001Api.qualityGates(),
        experienceDesignXdb001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setDomains(dom); setProviders(prov);
      setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await experienceDesignXdb001Api.validate({
        consistency_passed: true, accessibility_wcag_aa_met: true,
        cognitive_load_optimized: true, motion_standards_met: true,
        performance_perception_met: true, design_qa_passed: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Experience Design Bible passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Experience Modules' },
    { id: 'domains', label: 'Experience Domains' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-violet-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>XDB-000001 · P0 · Mandatory Before Internal Alpha</p>
        <h1 className="text-2xl font-semibold">Experience Design Bible</h1>
        <p className={`text-sm ${muted}`}>UX, visual design, motion, accessibility, cognitive psychology and MOS experience standards — reduce cognitive load, increase confidence.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/customer-value-cvd001" className={`${accent} hover:underline`}>CVD-000001</Link>
          <span className={muted}>→</span>
          <Link to="/experience-design" className={`${accent} hover:underline`}>Live Experience Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-violet-300 bg-violet-50 p-3 text-sm text-violet-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-violet-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
        <button type="button" onClick={validate} disabled={busy} className="rounded-lg bg-violet-700 px-3 py-1.5 text-sm text-white disabled:opacity-50">Validate Sprint</button>
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Objectives', value: dashboard.objectives },
            { label: 'Experience Modules', value: dashboard.experience_modules },
            { label: 'Experience Domains', value: dashboard.experience_domains },
            { label: 'Quality Gates', value: dashboard.quality_gates },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'modules' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {modules.map((m) => (
            <div key={m.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{m.name}</p>
              <p className={`text-xs ${muted}`}>{m.category} · {m.key}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'domains' && (
        <div className="grid gap-2 sm:grid-cols-3">
          {domains.map((d) => (
            <div key={d.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{d.name}</p>
              <p className={`text-xs ${muted}`}>{d.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'providers' && (
        <div className="grid gap-2">
          {providers.map((p) => (
            <div key={p.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{p.name}</p>
              <p className={`text-xs font-mono ${muted}`}>{p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {jobs.map((j) => (
            <div key={j.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{j.name}</p>
              <p className={`text-xs ${muted}`}>{j.schedule ?? 'on-demand'}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'gates' && (
        <div className="space-y-2">
          {gates.map((g) => (
            <div key={g.key} className={`flex items-center justify-between rounded-lg border p-3 ${card}`}>
              <span>{g.name}</span>
              {g.blocking && <span className="text-xs text-red-600">blocking</span>}
            </div>
          ))}
          {validation && (
            <p className={`text-sm ${validation.valid ? 'text-violet-600' : 'text-red-600'}`}>
              Validation: {validation.valid ? 'PASS' : `FAIL — ${validation.gaps?.join(', ')}`}
            </p>
          )}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <pre className="overflow-x-auto text-xs">{JSON.stringify(status, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
